<script setup>
import { nextTick, onMounted, onUnmounted, ref, useId } from "vue";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  panelId: { type: String, default: "" },
  triggerLabel: { type: String, required: true },
  panelLabel: { type: String, required: true },
  triggerClass: { type: [String, Array, Object], default: "" },
  panelClass: { type: [String, Array, Object], default: "" },
  panelTag: { type: String, default: "div" },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue"]);

const generatedId = useId();
const resolvedPanelId = props.panelId || `ui-menu-${generatedId}`;
const rootRef = ref(null);
const triggerRef = ref(null);
const panelRef = ref(null);

function menuItems() {
  if (!panelRef.value) return [];
  return Array.from(
    panelRef.value.querySelectorAll(
      '[role="menuitem"]:not([aria-disabled="true"]), [role="menuitemradio"]:not([aria-disabled="true"]), [role="menuitemcheckbox"]:not([aria-disabled="true"])',
    ),
  ).filter((item) => !item.disabled);
}

async function openMenu(focusPosition) {
  if (props.disabled) return;
  emit("update:modelValue", true);
  if (!focusPosition) return;
  await nextTick();
  const items = menuItems();
  items[focusPosition === "last" ? items.length - 1 : 0]?.focus();
}

function closeMenu(restoreFocus = false) {
  emit("update:modelValue", false);
  if (restoreFocus) triggerRef.value?.focus();
}

function toggleMenu() {
  if (props.modelValue) closeMenu();
  else void openMenu();
}

function onDocumentMouseDown(event) {
  if (props.modelValue && !rootRef.value?.contains(event.target)) closeMenu();
}

function onTriggerKeydown(event) {
  if (event.key === "Escape" && props.modelValue) {
    event.preventDefault();
    closeMenu(true);
    return;
  }
  if (!["ArrowDown", "ArrowUp"].includes(event.key)) return;
  event.preventDefault();
  void openMenu(event.key === "ArrowUp" ? "last" : "first");
}

function onPanelKeydown(event) {
  if (event.key === "Escape") {
    event.preventDefault();
    closeMenu(true);
    return;
  }
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  const items = menuItems();
  const currentIndex = items.indexOf(event.target);
  if (currentIndex === -1) return;
  event.preventDefault();
  let nextIndex;
  if (event.key === "Home") nextIndex = 0;
  else if (event.key === "End") nextIndex = items.length - 1;
  else if (event.key === "ArrowDown")
    nextIndex = (currentIndex + 1) % items.length;
  else nextIndex = (currentIndex - 1 + items.length) % items.length;
  items[nextIndex]?.focus();
}

function onFocusout(event) {
  if (!props.modelValue || rootRef.value?.contains(event.relatedTarget)) return;
  closeMenu();
}

onMounted(() => document.addEventListener("mousedown", onDocumentMouseDown));
onUnmounted(() =>
  document.removeEventListener("mousedown", onDocumentMouseDown),
);
</script>

<template>
  <div ref="rootRef" v-bind="$attrs" @focusout="onFocusout">
    <button
      ref="triggerRef"
      type="button"
      class="min-h-11 min-w-11"
      :class="triggerClass"
      :aria-label="triggerLabel"
      aria-haspopup="menu"
      :aria-expanded="modelValue"
      :aria-controls="resolvedPanelId"
      :disabled="disabled"
      @click="toggleMenu"
      @keydown="onTriggerKeydown"
    >
      <slot name="trigger" :open="modelValue" />
    </button>
    <component
      :is="panelTag"
      v-if="modelValue"
      :id="resolvedPanelId"
      ref="panelRef"
      role="menu"
      :aria-label="panelLabel"
      :class="panelClass"
      @keydown="onPanelKeydown"
    >
      <slot name="panel" :close="closeMenu" />
    </component>
  </div>
</template>
